import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { pick } from "lodash-es";
import { PlusIcon, XIcon } from "lucide-react";

import { Button } from "@/components/base/button";
import {
  Disclosure,
  DisclosureHeader,
  DisclosurePanel,
} from "@/components/base/disclosure";
import { useAppForm } from "@/components/base/form";
import { title } from "@/components/base/primitives";
import { divisionsQueryOptions } from "@/data/divisions";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { isNotNullOrUndefined } from "@/utils/types";

export type DivisionFormProps = {
  tournamentId: number;
  divisionId?: number;
  showTitle?: boolean;
  onCancel: () => void;
};

export function DivisionForm({
  tournamentId,
  divisionId,
  showTitle = true,
  onCancel,
}: DivisionFormProps) {
  const { data: divisions } = useSuspenseQuery({
    ...divisionsQueryOptions(),
  });

  const divisionOptions = divisions.map(({ id, display, name, maxAge }) => ({
    value: id,
    display: display ?? name.toUpperCase(),
    hasMaxAge: isNotNullOrUndefined(maxAge),
  }));

  const trpc = useTRPC();

  const { data: registrationSettings } = useQuery(
    trpc.settings.getRegistrationSettings.queryOptions(),
  );

  const { data: tournament } = useSuspenseQuery(
    trpc.tournaments.get.queryOptions({ id: tournamentId }),
  );

  const { data: editDivision } = useSuspenseQuery({
    ...trpc.tournaments.getDivisionRequirements.queryOptions({ tournamentId }),
    select: (data) => data?.find(({ id }) => divisionId === id),
  });

  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.upsertDivision.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onCancel();
    },
  });

  const form = useAppForm({
    defaultValues: {
      id: divisionId,
      divisionId: editDivision?.divisionId,
      name: editDivision?.name,
      gender: editDivision?.gender,
      capacity: editDivision?.capacity ?? 10,
      waitlistCapacity: editDivision?.waitlistCapacity ?? 5,
      autopromoteWaitlist:
        editDivision?.autopromoteWaitlist ??
        tournament?.venue.defaultAutopromoteWaitlist ??
        true,
      teamSize: editDivision?.teamSize ?? 2,
      displayGender: editDivision?.displayGender ?? false,
      displayDivision: editDivision?.displayDivision ?? false,
      registrationPrice: editDivision?.registrationPrice ?? null,
      requirements: editDivision?.requirements,
    },
    listeners: {
      onChange: ({ fieldApi, formApi }) => {
        if (fieldApi.name === "name") {
          const name = fieldApi.state.value as string | undefined;
          if (name) {
            formApi.setFieldValue("displayGender", true);
            formApi.setFieldValue("displayDivision", true);
          }
        }
      },
    },
    onSubmit: ({
      value: {
        id,
        divisionId,
        name,
        gender,
        capacity,
        waitlistCapacity,
        autopromoteWaitlist,
        teamSize,
        requirements,
        displayGender,
        displayDivision,
        registrationPrice,
      },
    }) => {
      mutate({
        id,
        tournamentId,
        name,
        divisionId,
        gender,
        capacity,
        waitlistCapacity,
        autopromoteWaitlist,
        teamSize,
        displayGender,
        displayDivision,
        registrationPrice,
        requirements,
      });
    },
  });

  return (
    <form
      className="grid grid-cols-6 gap-3"
      onSubmit={(e) => {
        e.preventDefault();

        form.handleSubmit();
      }}
    >
      {showTitle && (
        <h3 className={title({ size: "xs", class: "col-span-full" })}>
          {divisionId ? "Edit Division" : "Add Division"}
        </h3>
      )}

      {failureReason && (
        <form.AppForm>
          <form.Alert
            className="col-span-full"
            title={`Unable to ${divisionId ? "edit" : "add"} division.`}
            description={failureReason.message}
          />
        </form.AppForm>
      )}

      <form.Subscribe selector={(state) => [state.values.divisionId]}>
        {([divisionId]) => {
          const selectedDivision = divisionOptions.find(
            ({ value }) => value === divisionId,
          );

          return (
            <form.AppField
              name="gender"
              children={(field) => (
                <field.Select
                  isRequired={true}
                  label="Gender"
                  className="col-span-full sm:col-span-3"
                  field={field}
                  options={[
                    {
                      value: "male",
                      display: selectedDivision
                        ? selectedDivision.hasMaxAge
                          ? "Boy's"
                          : "Men's"
                        : "Men's/Boy's",
                    },
                    {
                      value: "female",
                      display: selectedDivision
                        ? selectedDivision.hasMaxAge
                          ? "Girl's"
                          : "Women's"
                        : "Women's/Girl's",
                    },
                    {
                      value: "coed",
                      display: "Coed",
                    },
                  ]}
                  placeholder="Select"
                />
              )}
            />
          );
        }}
      </form.Subscribe>

      <form.AppField
        name="divisionId"
        children={(field) => (
          <field.Select
            isRequired={true}
            label="Division"
            field={field}
            options={divisionOptions}
            placeholder="Select a division"
            className="col-span-3"
          />
        )}
      />

      <Disclosure className="col-span-full" card={false}>
        <DisclosureHeader size="sm" card={false}>
          <span>Extra Options</span>
        </DisclosureHeader>

        <DisclosurePanel
          card={false}
          contentClassName="col-span-full grid grid-cols-6 gap-3"
        >
          <form.Subscribe
            selector={(state) => ({
              values: state.values,
            })}
          >
            {({
              values: { name, gender, teamSize, divisionId, ...values },
            }) => {
              const division = divisions.find(({ id }) => id === divisionId);

              const placeholder =
                division && gender && teamSize
                  ? getTournamentDivisionDisplay({
                      division,
                      name: name ?? null,
                      gender,
                      teamSize,
                      ...values,
                    })
                  : undefined;

              return (
                <>
                  <form.AppField
                    name="name"
                    children={(field) => (
                      <field.Text
                        label={
                          <>
                            Name: <span>{placeholder ?? "-"}</span>
                          </>
                        }
                        field={field}
                        placeholder={placeholder}
                        className="col-span-4"
                      />
                    )}
                  />
                  <form.AppField
                    name="teamSize"
                    children={(field) => (
                      <field.Number
                        isRequired={true}
                        label="Team Size"
                        className="col-span-full sm:col-span-2"
                        field={field}
                      />
                    )}
                  />
                  <div className="col-span-full flex flex-row space-x-2">
                    <form.AppField
                      name="displayGender"
                      children={(field) => (
                        <field.Checkbox
                          label={<>Display gender</>}
                          field={field}
                          className="col-span-3"
                          isDisabled={!name}
                        />
                      )}
                    />
                    <form.AppField
                      name="displayDivision"
                      children={(field) => (
                        <field.Checkbox
                          label={<>Display division</>}
                          field={field}
                          className="col-span-3"
                          isDisabled={!name}
                        />
                      )}
                    />
                  </div>
                </>
              );
            }}
          </form.Subscribe>

          <form.AppField
            name="capacity"
            children={(field) => (
              <field.Number
                isRequired={true}
                label="Capacity"
                className="col-span-full sm:col-span-3"
                field={field}
              />
            )}
          />

          <form.AppField
            name="waitlistCapacity"
            children={(field) => (
              <field.Number
                isRequired={true}
                label="Waitlist Capacity"
                className="col-span-full sm:col-span-3"
                field={field}
              />
            )}
          />

          <form.AppField
            name="autopromoteWaitlist"
            children={(field) => (
              <field.Checkbox
                className="col-span-full"
                label="Auto Promote from Waitlist"
                field={field}
              />
            )}
          />

          <form.Subscribe selector={(state) => state.values.divisionId}>
            {(selectedDivisionId) => {
              const selectedDivision = divisions.find(
                ({ id }) => id === selectedDivisionId,
              );
              const defaultPrice =
                selectedDivision?.name === "open"
                  ? registrationSettings?.defaultOpenPrice
                  : registrationSettings?.defaultPrice;

              return (
                <form.AppField
                  name="registrationPrice"
                  children={(field) => (
                    <field.Number
                      label="Registration Price"
                      className="col-span-full sm:col-span-3"
                      field={field}
                      placeholder={
                        defaultPrice != null
                          ? `Default: $${defaultPrice}`
                          : undefined
                      }
                    />
                  )}
                />
              );
            }}
          </form.Subscribe>
        </DisclosurePanel>
      </Disclosure>

      <Disclosure className="col-span-full" card={false}>
        <DisclosureHeader
          size="sm"
          card={false}
          info={
            <>
              Add requirements to run special formats such as Mother/Daughter,
              coed, etc.
            </>
          }
        >
          <span>Special Requirements</span>
        </DisclosureHeader>

        <DisclosurePanel card={false}>
          <form.AppField name="requirements" mode="array">
            {(field) => (
              <div className="col-span-full grid grid-cols-6 gap-3">
                <div className="col-span-full flex flex-col items-stretch space-y-2">
                  {field.state.value?.map((req, i) => {
                    const selectedDivision = divisionOptions.find(
                      ({ value }) => value === req.qualifiedDivisionId,
                    );

                    return (
                      <div
                        key={`req-${req.qualifiedDivisionId}-${req.gender}-${i}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">
                            Player {i + 1} requirement
                          </span>
                          <Button
                            variant="icon"
                            size="sm"
                            onPress={() => {
                              field.handleChange((curr) =>
                                (curr ?? []).filter((_, idx) => idx !== i),
                              );
                            }}
                          >
                            <XIcon size={14} />
                          </Button>
                        </div>
                        <form.AppField name={`requirements[${i}].gender`}>
                          {(subField) => (
                            <>
                              <subField.Select
                                label="Gender"
                                field={subField}
                                options={[
                                  {
                                    value: "male",
                                    display: selectedDivision?.hasMaxAge
                                      ? "Boy's"
                                      : "Men's",
                                  },
                                  {
                                    value: "female",
                                    display: selectedDivision?.hasMaxAge
                                      ? "Girl's"
                                      : "Women's",
                                  },
                                ]}
                                placeholder="Select a gender"
                                className="col-span-full"
                              />
                            </>
                          )}
                        </form.AppField>
                        <form.AppField
                          name={`requirements[${i}].qualifiedDivisionId`}
                        >
                          {(subField) => (
                            <subField.Select
                              label="Division"
                              field={subField}
                              options={divisionOptions}
                              placeholder="Select a division"
                              className="col-span-full"
                            />
                          )}
                        </form.AppField>
                      </div>
                    );
                  })}
                  <form.Subscribe
                    selector={(state) =>
                      pick(state.values, ["requirements", "teamSize", "gender"])
                    }
                  >
                    {({ teamSize, requirements }) => (
                      <Button
                        className="cols-span-full"
                        size="sm"
                        isDisabled={
                          requirements ? requirements.length >= teamSize : false
                        }
                        onPress={() => {
                          field.handleChange((curr) =>
                            (curr ?? []).concat({
                              gender: null,
                              qualifiedDivisionId: null,
                              minimum: 1,
                              tournamentDivisionId: divisionId,
                            }),
                          );
                        }}
                      >
                        <PlusIcon size={16} /> Add requirement
                      </Button>
                    )}
                  </form.Subscribe>
                </div>
              </div>
            )}
          </form.AppField>
        </DisclosurePanel>
      </Disclosure>

      <form.AppForm>
        <form.Footer>
          <Button onPress={onCancel}>Cancel</Button>

          <form.SubmitButton>Submit</form.SubmitButton>
        </form.Footer>
      </form.AppForm>
    </form>
  );
}
